import { useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import {
  UserPlus, UserCheck, CopyX, AlertTriangle, Loader2, FileSpreadsheet, GraduationCap,
} from 'lucide-react';

/* Pre-import summary — shows what the upload WILL do before committing. */
export const ImportPreviewDialog = ({ preview, fileName, importing, onConfirm, onCancel, batchName }) => {
  const [showNotes, setShowNotes] = useState(false);
  if (!preview) return null;
  const enrolled = preview.enrolled_existing || 0;
  const added = (preview.created || 0) + enrolled;
  const stats = [
    { label: 'New trainees', value: preview.created, icon: UserPlus, cls: 'text-emerald-600 bg-emerald-500/10' },
    { label: 'Returning agents (joining this batch)', value: enrolled, icon: GraduationCap, cls: 'text-emerald-700 bg-emerald-500/10' },
    { label: 'Already here — updated', value: preview.updated, icon: UserCheck, cls: 'text-sky-600 bg-sky-500/10' },
    { label: 'Duplicates ignored', value: preview.duplicates || 0, icon: CopyX, cls: 'text-amber-600 bg-amber-500/10' },
    { label: 'Rows with errors', value: preview.skipped, icon: AlertTriangle, cls: 'text-rose-600 bg-rose-500/10' },
  ];
  const enrolNotes = (preview.warnings || []).filter(w => w.kind === 'enrolled_existing');
  const otherWarnings = (preview.warnings || []).filter(w => w.kind !== 'enrolled_existing');
  const willWrite = (preview.created + preview.updated + enrolled) > 0;
  return (
    <Dialog open onOpenChange={(o) => !o && onCancel()}>
      <DialogContent className="max-w-md" data-testid="import-preview-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5" /> Import preview
          </DialogTitle>
          <DialogDescription className="truncate">{fileName}</DialogDescription>
        </DialogHeader>
        <p className="text-sm rounded-md border bg-muted/30 p-3" data-testid="preview-headline">
          {added > 0 ? (
            <>This import will add <strong>{added} agent{added === 1 ? '' : 's'}</strong> to <strong>{batchName || 'their batches'}</strong>
              {' '}— {preview.created} brand new, {enrolled} returning from earlier batches
              {preview.updated ? <> — and update {preview.updated} already here</> : null}.</>
          ) : preview.updated ? (
            <>No new agents: {preview.updated} already on this roster will be updated.</>
          ) : (
            <>Nothing to import — check the rows with errors below.</>
          )}
        </p>
        <div className="grid grid-cols-2 gap-2">
          {stats.map(s => (
            <div key={s.label} className={`rounded-lg p-3 ${s.cls}`} data-testid={`preview-stat-${s.label.toLowerCase().replace(/ /g, '-')}`}>
              <div className="flex items-center gap-2">
                <s.icon className="w-4 h-4" />
                <span className="text-2xl font-bold">{s.value}</span>
              </div>
              <div className="text-xs mt-0.5 opacity-80">{s.label}</div>
            </div>
          ))}
        </div>
        {enrolNotes.length > 0 && (
          <div className="rounded-md border p-2 space-y-1" data-testid="preview-enrolled-notes">
            <p className="text-xs">
              Returning agents start the new batch with a clean status — only what is in this file is loaded.
              Their earlier batch record is kept.{' '}
              <button type="button" className="underline" onClick={() => setShowNotes(v => !v)}
                      data-testid="preview-enrolled-toggle">
                {showNotes ? 'Hide' : 'Show'} {enrolNotes.length} detail{enrolNotes.length === 1 ? '' : 's'}
              </button>
            </p>
            {showNotes && (
              <div className="max-h-24 overflow-y-auto space-y-0.5">
                {enrolNotes.map((w, i) => (
                  <p key={i} className="text-xs text-muted-foreground">Row {w.row}: {w.message}</p>
                ))}
              </div>
            )}
          </div>
        )}
        {preview.errors?.length > 0 && (
          <div className="max-h-32 overflow-y-auto rounded-md border bg-muted/30 p-2 space-y-1" data-testid="preview-errors">
            {preview.errors.slice(0, 10).map((e, i) => (
              <p key={i} className="text-xs text-rose-600">Row {e.row}: {e.message}</p>
            ))}
            {preview.errors.length > 10 && (
              <p className="text-xs text-muted-foreground">…and {preview.errors.length - 10} more</p>
            )}
          </div>
        )}
        {otherWarnings.length > 0 && (
          <div className="max-h-32 overflow-y-auto rounded-md border border-amber-300 bg-amber-50 p-2 space-y-1" data-testid="preview-warnings">
            {otherWarnings.slice(0, 10).map((w, i) => (
              <p key={i} className="text-xs text-amber-700">Row {w.row}: {w.message}</p>
            ))}
            {otherWarnings.length > 10 && (
              <p className="text-xs text-muted-foreground">…and {otherWarnings.length - 10} more</p>
            )}
          </div>
        )}
        <p className="text-xs text-muted-foreground">
          Nothing has been imported yet. People are matched by Cisco/Employee ID or email — never
          duplicated. Blanks never erase data.
        </p>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel} data-testid="import-preview-cancel-btn">Cancel</Button>
          <Button onClick={onConfirm} disabled={importing || !willWrite}
                  data-testid="import-preview-confirm-btn">
            {importing ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
            {added > 0 ? `Add ${added} agent${added === 1 ? '' : 's'}${batchName ? ' to this batch' : ''}` : 'Confirm import'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
